"use client";

import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

const WIDTH = 520;
const HEIGHT = 300;
const CX = WIDTH / 2;
const CY = HEIGHT / 2;
const LENGTH = 125;
const ARC = 44;
const RAD = Math.PI / 180;

export interface RayLabels {
  figureLabel: string;
  figureDescription: string;
  mediumTop: string;
  mediumBottom: string;
}

function point(angle: number, length: number, up: boolean): [number, number] {
  return [
    CX + length * Math.sin(angle * RAD),
    CY + (up ? -1 : 1) * length * Math.cos(angle * RAD),
  ];
}

/**
 * A short arc of radius `radius` between the normal and a ray at `angle` degrees from
 * it, on the left (side −1) or right (side 1) of the normal, above or below the surface.
 */
function arcPath(
  angle: number,
  radius: number,
  up: boolean,
  side: -1 | 1
): string {
  const startY = CY + (up ? -radius : radius);
  const endX = CX + side * radius * Math.sin(angle * RAD);
  const endY = CY + (up ? -1 : 1) * radius * Math.cos(angle * RAD);
  // SVG sweep flag 1 runs clockwise: up-and-left needs counter-clockwise, and so on.
  const clockwise = up === (side === 1);
  return `M${px(CX)},${px(startY)} A${radius},${radius} 0 0 ${clockwise ? 1 : 0} ${px(endX)},${px(endY)}`;
}

/** Opacity of a medium's shading: denser media are tinted more. */
function tint(n: number): number {
  return Math.min(0.1 + 0.18 * (n - 1), 0.55);
}

/**
 * A ray diagram: the incident ray from the upper left, the refracted ray below the
 * surface (absent under total internal reflection) and a faint reflected ray, with the
 * angles measured from the dashed normal.
 */
export function RayDiagram({
  n1,
  n2,
  theta1,
  theta2,
  labels,
}: {
  n1: number;
  n2: number;
  theta1: number;
  /** Null for total internal reflection. */
  theta2: number | null;
  labels: RayLabels;
}) {
  const incident = point(-theta1, LENGTH, true);
  const reflected = point(theta1, LENGTH, true);
  const refracted = theta2 === null ? null : point(theta2, LENGTH, false);
  const [labelX, labelY] = point(-theta1 / 2, ARC + 16, true);
  const refractedLabel =
    theta2 === null ? null : point(theta2 / 2, ARC + 22, false);
  return (
    <ChartSvg
      className="mx-auto block h-auto w-full max-w-xl"
      description={labels.figureDescription}
      height={HEIGHT}
      id="snell-figure"
      label={labels.figureLabel}
      width={WIDTH}
    >
      <defs>
        {[CHART_COLORS[0], CHART_COLORS[3], CHART_COLORS[2]].map((color) => (
          <marker
            id={`snell-arrow-${color.slice(1)}`}
            key={color}
            markerHeight={10}
            markerUnits="userSpaceOnUse"
            markerWidth={12}
            orient="auto-start-reverse"
            refX={9}
            refY={5}
          >
            <path d="M0,0 L11,5 L0,10 z" fill={color} />
          </marker>
        ))}
      </defs>
      <rect
        fill={CHART_COLORS[4]}
        fillOpacity={tint(n1)}
        height={CY}
        width={WIDTH}
        x={0}
        y={0}
      />
      <rect
        fill={CHART_COLORS[4]}
        fillOpacity={tint(n2)}
        height={CY}
        width={WIDTH}
        x={0}
        y={CY}
      />
      <line
        stroke={CHART_INK.text}
        strokeWidth={2}
        x1={0}
        x2={WIDTH}
        y1={CY}
        y2={CY}
      />
      <line
        stroke={CHART_INK.muted}
        strokeDasharray="6 5"
        strokeWidth={1.5}
        x1={CX}
        x2={CX}
        y1={18}
        y2={HEIGHT - 18}
      />
      <line
        markerEnd={`url(#snell-arrow-${CHART_COLORS[2].slice(1)})`}
        stroke={CHART_COLORS[2]}
        strokeDasharray="7 5"
        strokeOpacity={0.8}
        strokeWidth={2}
        x1={CX}
        x2={px(reflected[0])}
        y1={CY}
        y2={px(reflected[1])}
      />
      <line
        markerEnd={`url(#snell-arrow-${CHART_COLORS[0].slice(1)})`}
        stroke={CHART_COLORS[0]}
        strokeWidth={3}
        x1={px(incident[0])}
        x2={CX - 1}
        y1={px(incident[1])}
        y2={CY - 1}
      />
      {refracted === null ? null : (
        <line
          markerEnd={`url(#snell-arrow-${CHART_COLORS[3].slice(1)})`}
          stroke={CHART_COLORS[3]}
          strokeWidth={3}
          x1={CX}
          x2={px(refracted[0])}
          y1={CY}
          y2={px(refracted[1])}
        />
      )}
      <path
        d={arcPath(theta1, ARC, true, -1)}
        fill="none"
        stroke={CHART_INK.text}
        strokeWidth={1.5}
      />
      <text
        fill={CHART_INK.text}
        fontSize={13}
        textAnchor="middle"
        x={px(labelX)}
        y={px(labelY)}
      >
        θ₁
      </text>
      {theta2 === null || refractedLabel === null ? null : (
        <>
          <path
            d={arcPath(theta2, ARC, false, 1)}
            fill="none"
            stroke={CHART_INK.text}
            strokeWidth={1.5}
          />
          <text
            fill={CHART_INK.text}
            fontSize={13}
            textAnchor="middle"
            x={px(refractedLabel[0] + 10)}
            y={px(refractedLabel[1] + 6)}
          >
            θ₂
          </text>
        </>
      )}
      <text fill={CHART_INK.text} fontSize={13} fontWeight={600} x={14} y={24}>
        {labels.mediumTop}
      </text>
      <text
        fill={CHART_INK.text}
        fontSize={13}
        fontWeight={600}
        x={14}
        y={HEIGHT - 14}
      >
        {labels.mediumBottom}
      </text>
    </ChartSvg>
  );
}
